import { apiClient } from '../utils/apiClient';

let saProxyInvestorId: number | null = null;
let saProxyJourneyId: string | null = null;

/** Optional boot-out callback set by the ServiceAgentProxyWrapper — invoked when
 *  the server returns 403 on a proxied request, indicating the delegation was
 *  revoked or expired mid-session.  The wrapper uses this to navigate away
 *  from the proxy screen and show an inline toast. */
let proxyRevokedCallback: (() => void) | null = null;

export const setSAProxyMode = (investorId: number | null) => {
  saProxyInvestorId = investorId;
  // NOTE: journey scope is intentionally NOT cleared here. Navigating between wrapped SA routes
  // (journey picker → the journey's pages) briefly unmounts/remounts ServiceAgentProxyWrapper, and
  // clearing here would wipe the journey the agent just opened. The picker resets it on entry
  // (setSAProxyJourneyId(null)); a proxied 403 also clears it (see handleProxyError).
};

export const getSAProxyInvestorId = (): number | null => {
  return saProxyInvestorId;
};

/**
 * B1: the product journey (ss_investorproductid) the agent is currently working within. When set, it
 * tags proxied calls with `?journeyId=…` so the backend can enforce the per-product delegation wall
 * (Stage B). Null = identity-level (profile/KYC) — no journey scope.
 */
const SA_JOURNEY_STORE_PREFIX = 'fs.saProxyJourney.';

export const setSAProxyJourneyId = (journeyId: string | null) => {
  saProxyJourneyId = journeyId;
  // Refresh/deep-link survival (2026-09-08): this module variable dies with the page, so a
  // refreshed /profile lost the journey and every call — including Final Submit — silently
  // fell back to the identity-level path. Persist per INVESTOR (same lifetime pattern as the
  // investor's own facilon.activeInvestorJourney) so investor 14's journey can never leak
  // onto investor 19. The picker's reset (null) clears the stored value too.
  try {
    if (saProxyInvestorId !== null) {
      const key = SA_JOURNEY_STORE_PREFIX + saProxyInvestorId;
      if (journeyId) {
        sessionStorage.setItem(key, journeyId);
      } else {
        sessionStorage.removeItem(key);
      }
    }
  } catch { /* storage unavailable — in-memory only, i.e. the old behaviour */ }
};

/** Restore the stored journey for this investor after a refresh/deep-link (wrapper calls it). */
export const restoreSAProxyJourney = (investorId: number): void => {
  try {
    const stored = sessionStorage.getItem(SA_JOURNEY_STORE_PREFIX + investorId);
    if (stored) {
      saProxyJourneyId = stored;
    }
  } catch { /* storage unavailable — nothing to restore */ }
};

export const getSAProxyJourneyId = (): string | null => saProxyJourneyId;

/**
 * Register a callback that fires when a proxied request returns 403.
 * The ServiceAgentProxyWrapper registers this on mount so that a revoke /
 * expiry taking effect mid-session causes the SA to be evicted from the
 * investor view instead of hammering the server with forbidden calls.
 */
export const setProxyRevokedCallback = (fn: (() => void) | null) => {
  proxyRevokedCallback = fn;
};

const adaptPath = (originalUrl: string): string => {
  if (saProxyInvestorId === null) {
    return originalUrl;
  }

  const baseProxy = `/api/service-agents/me/investors/${saProxyInvestorId}`;

  // Onboarding/registration endpoints - don't proxy these (public endpoints)
  if (originalUrl.includes('/onboarding/register/')) {
    return originalUrl;
  }

  // Profile PDF (preview page): the two profile-booklet endpoints → SA proxy /pdf/*.
  // Must sit ABOVE the general /api/investor/ don't-proxy rule — unproxied, these hit
  // the investor controller with the AGENT's token ("Investor not found for user", then
  // a 401 that bounced the agent to B2C login; seen live 2026-09-05). Deliberately ONLY
  // these two: kyc-form/ckyc-form/fatca-form have no SA counterpart yet, so rewriting
  // them would trade today's failure for a 404 — left untouched.
  if (originalUrl.startsWith('/api/investor/pdf/print-preview')
      || originalUrl.startsWith('/api/investor/pdf/view')
      // Onboarding page forms (SA endpoints added 2026-09-06). Deliberately NOT
      // /kyc-form/preview or /kyc-form/data — those have no SA counterpart.
      // onboarding-pack = "Download all" zip (SA endpoint added 2026-10-25).
      || /^\/api\/investor\/pdf\/(kyc-form|ckyc-form|fatca-form|onboarding-pack)(\?|$)/.test(originalUrl)) {
    const path = originalUrl.replace('/api/investor/pdf/', '/pdf/');
    return `${baseProxy}${path}`;
  }

  // Investor specific endpoints (password change, etc.)
  if (originalUrl.startsWith('/api/investor/')) {
    return originalUrl;
  }

  // Profile endpoints: /api/clients/profile/* → /api/service-agents/me/investors/{id}/*
  if (originalUrl.startsWith('/api/clients/profile/')) {
    const path = originalUrl.replace('/api/clients/profile/', '/');
    return `${baseProxy}${path}`;
  }

  // Dashboard, progress, etc: /api/clients/me/* → /api/service-agents/me/investors/{id}/*
  if (originalUrl.startsWith('/api/clients/me/')) {
    const path = originalUrl.replace('/api/clients/me/', '/');
    return `${baseProxy}${path}`;
  }

  // Direct me endpoint: /api/clients/me → /api/service-agents/me/investors/{id}/profile
  if (originalUrl === '/api/clients/me') {
    return `${baseProxy}/profile`;
  }

  // KYC endpoints: /api/clients/kyc/* → /api/service-agents/me/investors/{id}/kyc/*
  if (originalUrl.startsWith('/api/clients/kyc/')) {
    const path = originalUrl.replace('/api/clients/kyc/', '/kyc/');
    return `${baseProxy}${path}`;
  }

  // Document endpoints: /api/clients/me/documents → /api/service-agents/me/investors/{id}/documents
  // Includes /documents, /documents/kyc/requirements, /documents/onboarding/list, etc.
  if (originalUrl.startsWith('/api/clients/me/documents')) {
    const path = originalUrl.replace('/api/clients/me/documents', '/documents');
    return `${baseProxy}${path}`;
  }

  // Fallback for direct /api/clients/documents (less common)
  if (originalUrl.startsWith('/api/clients/documents')) {
    const path = originalUrl.replace('/api/clients/documents', '/documents');
    return `${baseProxy}${path}`;
  }

  return originalUrl;
};

/**
 * Public adapter: rewrite the client URL to the SA proxy path (via {@link adaptPath}), then — when the
 * agent is working within a specific journey — tag the already-proxied call with `journeyId` so the
 * backend can enforce the per-product delegation wall (Stage B). Identity endpoints ignore the param.
 */
export const adaptUrl = (originalUrl: string): string => {
  const adapted = adaptPath(originalUrl);
  if (saProxyJourneyId && adapted !== originalUrl && !adapted.includes('selectedJourneyId=')) {
    return adapted + (adapted.includes('?') ? '&' : '?') + 'selectedJourneyId=' + encodeURIComponent(saProxyJourneyId);
  }
  return adapted;
};

/**
 * A 403 that refuses ONE action the delegation doesn't cover — outside the access scope, or a
 * permission the scope doesn't grant. The delegation itself is still live, so the agent stays on
 * the investor and the caller shows the error (2026-10-05: these used to evict the agent with a
 * misleading "revoked or expired" toast). Anything else — no active delegation, expired, wrong
 * journey — is still treated as a revocation.
 */
function isActionNotPermitted(err: any): boolean {
  const data = err?.response?.data;
  if (data?.code === 'SCOPE_VIOLATION') return true;
  const msg = String(data?.error ?? data?.message ?? '');
  return msg.includes('Delegation does not grant permission');
}

/**
 * Shared handler for proxy-mode responses.  When the server returns 403 on a
 * request that was actually proxied (i.e. the URL was adapted), we treat it
 * as revocation-in-progress: fire the boot-out callback, clear proxy mode,
 * and re-throw so the caller still sees the error.
 *
 * <p>This is the client-side half of "revocation session invalidation" —
 * the server-side half is the per-request check in
 * {@code ServiceAgentAccessControlService} which already refuses forbidden
 * requests the moment {@code isActive = false} is written.  The SA's JWT
 * itself stays valid (we do not maintain a server-side blacklist), but
 * every subsequent proxy call will 403, so the SA's effective access
 * window after revocation is one request, not the JWT lifetime.
 */
function handleProxyError(err: any, wasProxied: boolean): never {
  const status = err?.response?.status;
  if (wasProxied && status === 403 && saProxyInvestorId !== null && !isActionNotPermitted(err)) {
    saProxyInvestorId = null;
    saProxyJourneyId = null;
    if (proxyRevokedCallback) {
      try { proxyRevokedCallback(); } catch { /* swallow callback errors */ }
    }
  }
  throw err;
}

export const saProxyApiClient = {
  async get<T = any>(url: string, config?: any): Promise<{ data: T; status: number }> {
    const adaptedUrl = adaptUrl(url);
    const wasProxied = adaptedUrl !== url;
    console.log('[SAProxy] GET:', url, '→', adaptedUrl);
    try {
      return await apiClient.get<T>(adaptedUrl, config);
    } catch (err) {
      handleProxyError(err, wasProxied);
    }
  },

  async post<T = any>(url: string, data?: any, config?: any): Promise<{ data: T; status: number }> {
    const adaptedUrl = adaptUrl(url);
    const wasProxied = adaptedUrl !== url;
    console.log('[SAProxy] POST:', url, '→', adaptedUrl);
    try {
      return await apiClient.post<T>(adaptedUrl, data, config);
    } catch (err) {
      handleProxyError(err, wasProxied);
    }
  },

  async put<T = any>(url: string, data?: any, config?: any): Promise<{ data: T; status: number }> {
    const adaptedUrl = adaptUrl(url);
    const wasProxied = adaptedUrl !== url;
    console.log('[SAProxy] PUT:', url, '→', adaptedUrl);
    try {
      return await apiClient.put<T>(adaptedUrl, data, config);
    } catch (err) {
      handleProxyError(err, wasProxied);
    }
  },

  async delete<T = any>(url: string, config?: any): Promise<{ data: T; status: number }> {
    const adaptedUrl = adaptUrl(url);
    const wasProxied = adaptedUrl !== url;
    console.log('[SAProxy] DELETE:', url, '→', adaptedUrl);
    try {
      return await apiClient.delete<T>(adaptedUrl, config);
    } catch (err) {
      handleProxyError(err, wasProxied);
    }
  },
};
